import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

const pageSizeMap = {
  A4:        { width: "210mm", height: "297mm" },
  Letter:    { width: "216mm", height: "279mm" },
  Legal:     { width: "216mm", height: "356mm" },
  A3:        { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Software Engineer",
  phone: "+91 98765 43210",
  email: "john.doe@email.com",
  city: "Chennai, India",
  website: "www.johndoe.com",
  linkedIn: "",
  drivingLicence: "",
  profile:
    "Experienced software engineer with a strong background in full-stack development, cloud solutions, and problem solving.",
  skills: [
    "React, Node.js, MongoDB",
    "Java, Spring Boot",
    "AWS, Docker",
    "Problem Solving",
  ],
};

const defaultSectionOrder = [
  "profile",
  "skills",
  "education",
  "experience",
  "projects",
  "languages",
  "certifications",
  "accomplishments",
  "softwareSkills",
  "volunteering",
  "interests",
  "websites",
  "references",
  "additionalInfo",
];

const Resume3 = ({
  color           = "#1a2b47",
  font            = "Arial",
  fontSize        = "14px",
  fontStyle       = "normal",
  headingSize     = "24px",
  sectionSpacing  = 20,   // slider 10-100 → raw px gap between sections
  paragraphSpacing = 10,  // slider 10-80  → raw px gap between paragraphs
  lineSpacing     = 20,   // slider 10-50  → raw px line-height
  topBottomMargin = 40,   // slider 10-100 → raw px top/bottom padding
  sideMargins     = 40,   // slider 10-100 → raw px left/right padding
  paragraphIndent = 0,
  lineWeight      = 1,
  pageSize        = "A4",
  formData        = {},
  photo           = "",
  workExperiences = [],
  exporting       = false,
  enableDragDrop  = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const data = { ...defaultData, ...formData };

  const [currentPage, setCurrentPage] = useState(0);
  const [pages,        setPages]       = useState([]);
  const resumeRef = useRef(null);

  const [educationEntries,     setEducationEntries]     = useState([]);
  const [savedSkills,          setSavedSkills]          = useState([]);
  const [savedSummary,         setSavedSummary]         = useState("");
  const [savedAdditionalInfo,  setSavedAdditionalInfo]  = useState([]);
  const [savedLanguages,       setSavedLanguages]       = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications,  setSavedCertifications]  = useState([]);
  const [savedReferences,      setSavedReferences]      = useState([]);
  const [softwareSkills,       setSoftwareSkills]       = useState([]);
  const [savedVolunteering,    setSavedVolunteering]    = useState([]);
  const [savedInterests,       setSavedInterests]       = useState([]);
  const [savedWebsites,        setSavedWebsites]        = useState([]);
  const [customSections,       setCustomSections]       = useState({});

  const [sectionOrder,      setSectionOrder]      = useState(defaultSectionOrder);
  const [dragOverIndex,     setDragOverIndex]     = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ── LocalStorage ────────────────────────────────────────────────────────
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo( JSON.parse(localStorage.getItem("additionalInfo")        || "[]"));
    setSavedLanguages(      JSON.parse(localStorage.getItem("languagesList")          || "[]"));
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")    || "[]"));
    setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList")     || "[]"));
    setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")         || "[]"));
    setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")         || "[]"));
    setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")           || "[]"));
    setSavedInterests(      JSON.parse(localStorage.getItem("interests")              || "[]"));
    setSavedWebsites(       JSON.parse(localStorage.getItem("websites")               || "[]"));

    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustom = JSON.parse(
        localStorage.getItem(`current_custom_sections_${userId}`) || "[]"
      );
      currentCustom.forEach(sectionName => {
        try {
          const raw = localStorage.getItem(`custom_${userId}_${sectionName}`);
          if (raw) {
            const parsed     = JSON.parse(raw);
            const validItems = (parsed.items || []).filter(i => i && i.trim() !== "");
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch {}
      });
    }
    setCustomSections(sectionsData);

    const savedOrder = localStorage.getItem("resume3SectionOrder");
    const customIds  = Object.keys(sectionsData).map(n => `custom_${n}`);
    if (savedOrder) {
      const parsed   = JSON.parse(savedOrder);
      const filtered = parsed.filter(s => !s.startsWith("custom_"));
      setSectionOrder([...filtered, ...customIds]);
    } else {
      setSectionOrder([...defaultSectionOrder, ...customIds]);
    }
  }, []);

  // Sync custom sections into order
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    setSectionOrder(prev => {
      const filtered = prev.filter(s => !s.startsWith("custom_"));
      const next     = [...filtered, ...customIds];
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem("resume3SectionOrder", JSON.stringify(next));
        return next;
      }
      return prev;
    });
  }, [customSections]);

  // Persist order
  useEffect(() => {
    if (sectionOrder.length > 0)
      localStorage.setItem("resume3SectionOrder", JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  const formatDate = (month, year) => (month && year ? `${month} ${year}` : "");

  // ── Drag handlers ────────────────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDragLeave = (e)         => { e.preventDefault(); setDragOverIndex(null); };
  const handleDragEnd   = (e)         => { setDragOverIndex(null); e.currentTarget.style.opacity = "1"; };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const { index: sourceIndex } = JSON.parse(e.dataTransfer.getData("text/plain"));
    if (sourceIndex !== targetIndex) {
      const arr = [...sectionOrder];
      const [m] = arr.splice(sourceIndex, 1);
      arr.splice(targetIndex, 0, m);
      setSectionOrder(arr);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = "1";
  };

  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);

  const getSectionTitle = (id) => {
    const titles = {
      profile: "Profile", skills: "Skills", education: "Education",
      experience: "Work Experience", projects: "Projects", languages: "Languages",
      certifications: "Certifications", accomplishments: "Accomplishments",
      softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles",
      references: "References", additionalInfo: "Additional Information",
    };
    if (id.startsWith("custom_")) {
      const n = id.replace("custom_", "").replace(/_/g, " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    }
    return titles[id] || id;
  };

  // ── Spacing helpers — all use raw px, bypassing MUI multiplier ──────────
  // Every marginBottom / padding uses inline `style` or an explicit `px` string
  // so the slider value (e.g. sectionSpacing = 20) maps 1-to-1 to 20 px on screen.

  /** section wrapper bottom gap */
  const secGapStyle   = { marginBottom: `${sectionSpacing}px` };
  /** paragraph / entry bottom gap */
  const paraGapStyle  = { marginBottom: `${paragraphSpacing}px` };
  /** half-paragraph gap for list items */
  const itemGapStyle  = { marginBottom: `${Math.max(2, Math.round(paragraphSpacing / 2))}px` };

  /** shared text inline style */
  const baseText = {
    color,
    fontFamily:  font,
    fontSize,
    fontStyle,
    lineHeight:  `${lineSpacing}px`,
  };

  /** heading inline style */
  const headStyle = {
    ...baseText,
    fontSize:    headingSize,
    fontWeight:  "bold",
    marginBottom: `${paragraphSpacing}px`,
  };

  // ── Section content renderer ─────────────────────────────────────────────
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {

      // ── PROFILE ────────────────────────────────────────────────────────
      case "profile":
        return (
          <div className="resume-section" id="profile-section" style={secGapStyle}>
            <p style={headStyle}>Profile</p>
            <p style={{ ...baseText, textIndent: `${paragraphIndent}px`, margin: 0 }}>
              {savedSummary || data.profile}
            </p>
          </div>
        );

      // ── SKILLS ─────────────────────────────────────────────────────────
      case "skills":
        return (
          <div className="resume-section" id="skills-section" style={secGapStyle}>
            <p style={headStyle}>Skills</p>
            <ul style={{ paddingLeft: "20px", margin: 0, listStyleType: "none" }}>
              {(savedSkills.length > 0 ? savedSkills : data.skills).map((skill, i) => (
                <li key={i} style={{ ...baseText, ...itemGapStyle }}>
                  {typeof skill === "string" ? skill : `${skill.name}${skill.rating ? ` (${skill.rating}%)` : ""}`}
                </li>
              ))}
            </ul>
          </div>
        );

      // ── EDUCATION ──────────────────────────────────────────────────────
      case "education": {
        let entries = [];
        try {
          const forResume = localStorage.getItem("educationEntriesForResume");
          if (forResume) {
            entries = JSON.parse(forResume);
          } else {
            const stored = localStorage.getItem("educationEntries");
            if (stored) {
              entries = JSON.parse(stored).map(entry => {
                if (entry.educationLevel === "school") {
                  return {
                    id: entry.id,
                    degree: `${entry.standard || ""}${entry.standard ? " Standard" : ""}`,
                    fieldOfStudy: entry.stream || "General Education",
                    schoolName: entry.schoolName || "",
                    schoolLocation: entry.schoolLocation || "",
                    gradMonth: "", gradYear: "",
                    additionalCoursework: [
                      entry.district,
                      entry.percentage && `Percentage: ${entry.percentage}%`,
                    ].filter(Boolean).join(" | "),
                  };
                }
                return {
                  id: entry.id,
                  degree: `${entry.degreeName || ""}${entry.programType ? ` (${entry.programType})` : ""}`,
                  fieldOfStudy: entry.specialization || entry.degreeType || "",
                  schoolName: entry.collegeName || "",
                  schoolLocation: entry.collegeLocation || "",
                  gradMonth: entry.gradMonth || "",
                  gradYear: entry.gradYear || "",
                  additionalCoursework: [
                    entry.cgpa        && `CGPA: ${entry.cgpa}`,
                    entry.percentage  && `Percentage: ${entry.percentage}%`,
                    entry.grade       && `Grade: ${entry.grade}`,
                    entry.additionalCoursework,
                  ].filter(Boolean).join(" | "),
                };
              });
            }
          }
        } catch {}
        return (
          <div className="resume-section" id="education-section" style={secGapStyle}>
            <p style={headStyle}>Education</p>
            {entries.length > 0 ? entries.map((edu) => (
              <div key={edu.id} style={paraGapStyle}>
                <p style={{ ...baseText, fontWeight: "bold", margin: 0 }}>
                  {edu.degree}{edu.fieldOfStudy ? ` - ${edu.fieldOfStudy}` : ""}
                </p>
                <p style={{ ...baseText, margin: 0 }}>
                  {edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ""}
                </p>
                {(edu.gradMonth || edu.gradYear) && (
                  <p style={{ ...baseText, fontStyle: "italic", margin: 0 }}>
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </p>
                )}
                {edu.additionalCoursework && (
                  <p style={{ ...baseText, margin: 0 }}>{edu.additionalCoursework}</p>
                )}
              </div>
            )) : (
              <div style={paraGapStyle}>
                <p style={{ ...baseText, fontWeight: "bold", margin: 0 }}>B.Tech in CSE</p>
                <p style={{ ...baseText, margin: 0 }}>XYZ University, 2014 - 2018</p>
              </div>
            )}
          </div>
        );
      }

      // ── EXPERIENCE ─────────────────────────────────────────────────────
      case "experience":
        return (
          <div className="resume-section" id="experience-section" style={secGapStyle}>
            <p style={headStyle}>Experience</p>
            {workExperiences && workExperiences.length > 0 ? (
              workExperiences.map((work, i) => (
                <div key={i} style={paraGapStyle}>
                  <p style={{ ...baseText, fontWeight: "bold", margin: 0 }}>{work.jobTitle}</p>
                  <p style={{ ...baseText, margin: 0 }}>
                    {work.employer} | {formatDate(work.startMonth, work.startYear)} -{" "}
                    {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </p>
                  {work.description && (
                    <div
                      style={{ ...baseText, marginTop: `${Math.round(paragraphSpacing / 4)}px` }}
                      dangerouslySetInnerHTML={{
                        __html: work.description
                          .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font};color:${color};line-height:${lineSpacing}px">$1</strong>`)
                          .replace(/\*(.*?)\*/g,     `<em style="font-family:${font};color:${color};line-height:${lineSpacing}px">$1</em>`)
                          .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font};color:${color};line-height:${lineSpacing}px">$1</u>`)
                          .replace(/\n/g,            "<br/>"),
                      }}
                    />
                  )}
                </div>
              ))
            ) : (
              <>
                {[
                  { title: "Senior Software Engineer", sub: "ABC Tech | 2020 - Present",  desc: "Leading a team of developers building scalable web applications." },
                  { title: "Software Developer",        sub: "XYZ Solutions | 2018 - 2020", desc: "Worked on REST APIs and frontend dashboards for enterprise apps." },
                ].map((item, i) => (
                  <div key={i} style={paraGapStyle}>
                    <p style={{ ...baseText, fontWeight: "bold", margin: 0 }}>{item.title}</p>
                    <p style={{ ...baseText, margin: 0 }}>{item.sub}</p>
                    <p style={{ ...baseText, margin: 0 }}>{item.desc}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        );

      // ── PROJECTS ───────────────────────────────────────────────────────
      case "projects":
        return (
          <div className="resume-section" id="projects-section" style={secGapStyle}>
            <p style={headStyle}>Projects</p>
            <ul style={{ paddingLeft: "20px", margin: 0, listStyleType: "none" }}>
              {[
                "E-commerce Platform with MERN stack",
                "AI-based Resume Screener",
                "Hospital Management System",
              ].map((proj, i) => (
                <li key={i} style={{ ...baseText, ...itemGapStyle }}>{proj}</li>
              ))}
            </ul>
          </div>
        );

      // ── LANGUAGES ──────────────────────────────────────────────────────
      case "languages":
        return savedLanguages.length > 0 ? (
          <div className="resume-section" id="languages-section" style={secGapStyle}>
            <p style={headStyle}>Languages</p>
            {savedLanguages.map((lang, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>
                {lang.name}{lang.level ? ` – ${lang.level}` : ""}
              </p>
            ))}
          </div>
        ) : null;

      // ── CERTIFICATIONS ─────────────────────────────────────────────────
      case "certifications":
        return savedCertifications.length > 0 ? (
          <div className="resume-section" id="certifications-section" style={secGapStyle}>
            <p style={headStyle}>Certifications</p>
            {savedCertifications.map((cert, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>
                {cert.name} – {cert.provider} ({cert.year})
              </p>
            ))}
          </div>
        ) : null;

      // ── ACCOMPLISHMENTS ────────────────────────────────────────────────
      case "accomplishments":
        return savedAccomplishments.length > 0 ? (
          <div className="resume-section" id="accomplishments-section" style={secGapStyle}>
            <p style={headStyle}>Accomplishments</p>
            {savedAccomplishments.map((acc, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0, whiteSpace: "pre-line" }}>{acc}</p>
            ))}
          </div>
        ) : null;

      // ── SOFTWARE SKILLS ────────────────────────────────────────────────
      case "softwareSkills":
        return softwareSkills.length > 0 ? (
          <div className="resume-section" id="software-skills-section" style={secGapStyle}>
            <p style={headStyle}>Software Skills</p>
            {softwareSkills.map((skill, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>
                {skill.name} — {skill.level}%
              </p>
            ))}
          </div>
        ) : null;

      // ── VOLUNTEERING ───────────────────────────────────────────────────
      case "volunteering":
        return savedVolunteering.length > 0 ? (
          <div className="resume-section" id="volunteering-section" style={secGapStyle}>
            <p style={headStyle}>Volunteering</p>
            {savedVolunteering.map((vol, i) => (
              <div key={i} style={paraGapStyle}>
                <p style={{ ...baseText, fontWeight: "bold", margin: 0 }}>{vol.subtopic}</p>
                <p style={{ ...baseText, margin: 0 }}>{vol.fromDate} - {vol.toDate}</p>
                <p style={{ ...baseText, whiteSpace: "pre-line", margin: 0 }}>{vol.content}</p>
              </div>
            ))}
          </div>
        ) : null;

      // ── INTERESTS ──────────────────────────────────────────────────────
      case "interests":
        return savedInterests.length > 0 ? (
          <div className="resume-section" id="interests-section" style={secGapStyle}>
            <p style={headStyle}>Interests</p>
            {savedInterests.map((interest, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>• {interest}</p>
            ))}
          </div>
        ) : null;

      // ── WEBSITES ───────────────────────────────────────────────────────
      case "websites":
        return savedWebsites.length > 0 ? (
          <div className="resume-section" id="websites-section" style={secGapStyle}>
            <p style={headStyle}>Websites / Profiles</p>
            {savedWebsites.map((site, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>
                <a
                  href={site.url.startsWith("http") ? site.url : `https://${site.url}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color, textDecoration: "none", fontFamily: font, fontSize }}
                >
                  {site.name || site.url}
                </a>
                {site.addToHeader ? " (Shown in Header)" : ""}
              </p>
            ))}
          </div>
        ) : null;

      // ── REFERENCES ─────────────────────────────────────────────────────
      case "references":
        return savedReferences.length > 0 ? (
          <div className="resume-section" id="references-section" style={secGapStyle}>
            <p style={headStyle}>References</p>
            {savedReferences.map((ref, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0, whiteSpace: "pre-line" }}>{ref}</p>
            ))}
          </div>
        ) : null;

      // ── ADDITIONAL INFO ────────────────────────────────────────────────
      case "additionalInfo":
        return savedAdditionalInfo.length > 0 ? (
          <div className="resume-section" id="additional-info-section" style={secGapStyle}>
            <p style={headStyle}>Additional Information</p>
            {savedAdditionalInfo.map((info, i) => (
              <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0 }}>{info}</p>
            ))}
          </div>
        ) : null;

      // ── CUSTOM SECTIONS ────────────────────────────────────────────────
      default:
        if (sectionId && sectionId.startsWith("custom_")) {
          const name       = sectionId.replace("custom_", "");
          const validItems = (customSections[name] || []).filter(i => i && i.trim() !== "");
          return validItems.length > 0 ? (
            <div className="resume-section" id={`custom-${name}-section`} style={secGapStyle}>
              <p style={headStyle}>{name.replace(/_/g, " ").toUpperCase()}</p>
              {validItems.map((item, i) => (
                <p key={i} style={{ ...baseText, ...itemGapStyle, margin: 0, whiteSpace: "pre-line" }}>• {item}</p>
              ))}
            </div>
          ) : null;
        }
        return null;
    }
  };

  // ── Pagination (unchanged logic, just passes correct topBottomMargin) ──
  const splitContentIntoPages = useCallback(() => {
    if (!resumeRef.current) return;
    const sections        = Array.from(resumeRef.current.querySelectorAll(".resume-section"));
    const headerHeight    = resumeRef.current.querySelector(".resume-header")?.offsetHeight || 0;
    const pageHeightInPx  = parseFloat(page.height.replace("mm", "")) * 3.78;
    const availableFirst  = pageHeightInPx - headerHeight - 2 * topBottomMargin - 32;
    const availableRest   = pageHeightInPx - 2 * topBottomMargin - 32;

    let newPages = [], cur = [], curH = 0;
    sections.forEach(section => {
      const h     = section.offsetHeight;
      const limit = newPages.length === 0 ? availableFirst : availableRest;
      if (curH + h > limit) { newPages.push(cur); cur = [section.outerHTML]; curH = h; }
      else                  { cur.push(section.outerHTML); curH += h; }
    });
    if (cur.length > 0) newPages.push(cur);
    setPages(newPages);
  }, [
    page.height, topBottomMargin,
    workExperiences, educationEntries, savedSkills, savedSummary,
    savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, customSections, sectionOrder,
    sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color,
  ]);

  useEffect(() => {
    const id = setTimeout(splitContentIntoPages, 500);
    return () => clearTimeout(id);
  }, [splitContentIntoPages]);

  // ── Download helpers (unchanged) ────────────────────────────────────────
  const saveDownloadHistory = async (format, fileName, fileSize) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      await fetch("https://resume-maker-lc.onrender.com/api/resumes/save-download", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: 3, fileName, fileFormat: format, fileSize, themeColor: color, downloadedAt: new Date().toISOString() }),
      });
    } catch {}
  };

  const handlePDFDownload = async () => {
    const el = document.getElementById("resume-container");
    if (!el) return;
    try {
      const resumePages = el.querySelectorAll(".resume-page");
      if (!resumePages.length) return;
      const pdf = new jsPDF("p", "mm", pageSize.toLowerCase());
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < resumePages.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pw, (canvas.height * pw) / canvas.width, undefined, "FAST");
      }
      const blob = pdf.output("blob");
      await saveDownloadHistory("pdf", "resume", blob.size);
      pdf.save("resume.pdf");
    } catch { alert("Error generating PDF. Please try again."); }
  };

  const handlePNGDownload = async () => {
    const el = document.getElementById("resume-container");
    if (!el) return;
    try {
      const resumePages = el.querySelectorAll(".resume-page");
      if (!resumePages.length) return;
      for (let i = 0; i < resumePages.length; i++) {
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
        const link = document.createElement("a");
        link.download = resumePages.length === 1 ? "resume.png" : `resume-page-${i + 1}.png`;
        link.href = canvas.toDataURL();
        setTimeout(() => link.click(), i * 500);
      }
      await saveDownloadHistory("png", "resume", 0);
      if (resumePages.length > 1) alert(`Downloading ${resumePages.length} PNG files.`);
    } catch { alert("Error generating PNG files. Please try again."); }
  };

  // ── Page padding inline style (applied identically to measurement div and rendered pages) ──
  const pagePadStyle = {
    paddingTop:    `${topBottomMargin}px`,
    paddingBottom: `${topBottomMargin}px`,
    paddingLeft:   `${sideMargins}px`,
    paddingRight:  `${sideMargins}px`,
  };

  // ── Header block (shared between measurement div and page 0) ────────────
  const renderHeader = () => (
    <>
      <div className="resume-header" style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: `${paragraphSpacing}px` }}>
        {/* Photo */}
        <div style={{ flexShrink: 0 }}>
          <img
            src={photo || "https://randomuser.me/api/portraits/men/45.jpg"}
            alt="avatar"
            style={{ width: 120, height: 120, borderRadius: "50%", border: `${lineWeight}px solid ${color}`, objectFit: "cover" }}
          />
        </div>
        {/* Name + contact */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ ...baseText, fontSize: headingSize, fontWeight: "bold", margin: 0, lineHeight: `${lineSpacing}px` }}>
            {data.firstName} {data.lastName}
          </p>
          <p style={{ ...baseText, margin: 0 }}>{data.currentPosition}</p>
          <div style={{ marginTop: `${Math.round(paragraphSpacing / 2)}px` }}>
            {[
              { show: data.email,          icon: <EmailIcon    style={{ fontSize: 16, color, verticalAlign: "middle" }} />, val: data.email },
              { show: data.phone,          icon: <PhoneIcon    style={{ fontSize: 16, color, verticalAlign: "middle" }} />, val: data.phone },
              { show: data.city,           icon: <LocationOnIcon style={{ fontSize: 16, color, verticalAlign: "middle" }} />, val: data.city },
              { show: data.website,        icon: <LanguageIcon style={{ fontSize: 16, color, verticalAlign: "middle" }} />,
                val: <a href={data.website.startsWith("http") ? data.website : `https://${data.website}`} target="_blank" rel="noopener noreferrer"
                         style={{ color, textDecoration: "none", fontFamily: font, fontSize }}>
                       {data.website.replace(/^https?:\/\/(www\.)?/, "")}
                     </a> },
              { show: data.linkedIn,       icon: <LinkedInIcon style={{ fontSize: 16, color: "#0077b5", verticalAlign: "middle" }} />,
                val: <a href={data.linkedIn.startsWith("http") ? data.linkedIn : `https://${data.linkedIn}`} target="_blank" rel="noopener noreferrer"
                         style={{ color: "#0077b5", textDecoration: "none", fontFamily: font, fontSize }}>
                       {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, "")}
                     </a> },
              { show: data.drivingLicence, icon: <DriveEtaIcon style={{ fontSize: 16, color: "#4caf50", verticalAlign: "middle" }} />, val: `DL: ${data.drivingLicence}` },
            ].filter(r => r.show).map((row, i) => (
              <p key={i} style={{ ...baseText, display: "flex", alignItems: "center", gap: "4px", margin: 0, marginBottom: `${Math.round(paragraphSpacing / 4)}px` }}>
                {row.icon} {row.val}
              </p>
            ))}
          </div>
        </div>
      </div>
      {/* Divider */}
      <hr style={{ border: "none", borderTop: `${lineWeight}px solid ${color}`, margin: `${paragraphSpacing}px 0` }} />
    </>
  );

  // ── Drag dialog panel ────────────────────────────────────────────────────
  const renderDragPanel = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <DragIndicator sx={{ mr: 1 }} />
        Resume Sections ({sectionOrder.length})
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
        {sectionOrder.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: "flex", alignItems: "center",
              bgcolor: dragOverIndex === idx ? "#f0f0f0" : "white",
              boxShadow: dragOverIndex === idx ? 2 : 1,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              transition: "all 0.2s ease",
            }}>
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset Section Order
      </Button>
    </Box>
  );

  // ── Page box style — uses raw px via style prop ──────────────────────────
  const pageBoxStyle = (isVisible) => ({
    width:          page.width,
    minHeight:      page.height,
    margin:         "0 auto",
    boxShadow:      isVisible ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
    backgroundColor:"#fff",
    fontFamily:     font,
    fontSize,
    fontStyle,
    lineHeight:     `${lineSpacing}px`,
    color,
    display:        isVisible ? "block" : "none",
    ...pagePadStyle,
    // print
    pageBreakAfter: "always",
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box style={{ width: "100%", position: "relative", fontFamily: font, color }}>
      {/* Controls */}
      {!exporting && (
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 16, gap: 8, flexWrap: "wrap" }}
          sx={{ "@media print": { display: "none" } }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography style={{ fontFamily: font, color }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(pages.length - 1, p + 1))} disabled={currentPage >= Math.max(0, pages.length - 1)} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              <Button variant="outlined" color="secondary" size="small" onClick={resetSectionOrder}>
                Reset Section Order
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement div */}
      <div ref={resumeRef} style={{ visibility: "hidden", position: "absolute", left: -9999, top: 0, width: page.width, fontFamily: font, ...pagePadStyle }}>
        {renderHeader()}
        {sectionOrder.map(id => (
          <React.Fragment key={id}>{renderSectionContent(id)}</React.Fragment>
        ))}
      </div>

      {/* Rendered pages */}
      <div id="resume-container" style={{ fontFamily: font, color }}>
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <div
              key={index}
              className="resume-page"
              style={{
                ...pageBoxStyle(exporting || index === currentPage),
                display: exporting ? "block" : (index === currentPage ? "block" : "none"),
              }}
            >
              {/* Header only on page 0 */}
              {index === 0 && renderHeader()}

              {/* Page content injected via dangerouslySetInnerHTML */}
              <div
                dangerouslySetInnerHTML={{
                  __html: pageContent.join("").replace(
                    /style="([^"]*)"/g,
                    (match, p1) =>
                      `style="${p1}; font-family:${font}!important; color:${color}!important; line-height:${lineSpacing}px!important"`
                  ),
                }}
              />
            </div>
          ))
        ) : (
          // Loading placeholder
          <div style={{ ...pageBoxStyle(true), display: "flex", flexDirection: "column" }}>
            <p style={{ ...baseText, padding: "16px" }}>Loading resume...</p>
          </div>
        )}
      </div>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} /> Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop to reorder sections.
          </Typography>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume3;